import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import { FieldValues, UseFormSetValue } from 'react-hook-form'
import {
  addToaster,
  DataTypeStore,
  fetchDuplicateRecordsAPI,
  getEntityDataByEntityFilter,
  getLayoutByEntityIdAndTypeApi,
  hideLoader,
  LayoutResponseType,
  showLoader,
  transformToDuplicateApiPayload,
} from '@app-store'
import { CustomButton } from '@ui-kit'
import { AdvancedSearchTab } from '../AdvancedSearchTab'
import { AdvancedSearchInfo } from '../../pages/call-interaction/RequestorSearchForm'
import {
  BUTTONS,
  componentNames,
  ENTITY_ID,
  LAYOUT_TYPE_NAMESPACE,
  NAMESPACES,
} from '../../utils/constants'
import { APPLICATION_PREFERENCES_STRINGS } from '../../utils/strings'
import { ElementType } from '../../types/layout.types'
import { ListingTableRow } from '../../types/listingTable.types'
import {
  addEntityData,
  editEntityData,
  getEntityData,
} from '../../apis/entityData.api'
import { validatePhoneNumberAndAddress } from '../../utils/form.utils'
import {
  RootState,
  useAppDispatch,
  useAppSelector,
} from '../../utils/agent.store'
import { TOASTER_NAMESPACE } from '../../constants/addEditPage.strings'
import { isEmpty, noop, uniqueId } from 'lodash'
import {
  PatientAdvancedSearchPanelHandle,
  PatientAdvancedSearchPanelProps,
} from '../../utils/types'

const PATIENT_SEARCH_ELEMENT: ElementType = {
  id: 0,
  keyName: 'case_dedupe_patient',
  elementId: 'case_dedupe_patient',
  elementName: componentNames.SINGLE_LOOKUP,
  meta: {
    label: NAMESPACES.PATIENT_KEY,
    field_name: 'case_dedupe_patient',
    relationship: {
      target_entity: 'c_patients',
      target_entity_id: ENTITY_ID.PATIENT,
    },
  },
} as ElementType

export const PatientAdvancedSearchPanel = forwardRef<
  PatientAdvancedSearchPanelHandle,
  PatientAdvancedSearchPanelProps
>(
  (
    {
      isActive,
      programId,
      productId,
      isSelectionDisabled = false,
      breadcrumbLabel = `${NAMESPACES.PATIENT_KEY} ${APPLICATION_PREFERENCES_STRINGS.ADVANCED_SEARCH}`,
      onPatientSelected,
      onCancel,
      onProceedToIntake,
    },
    ref
  ) => {
    const dispatch = useAppDispatch()
    const entitiesByName = useAppSelector(
      (state: RootState) => state.entity.entitiesByName
    )
    const [viewEntityRecord, setViewEntityRecord] = useState<
      DataTypeStore | undefined
    >()
    const [advancedFiltersData, setAdvancedFiltersData] = useState<
      AdvancedSearchInfo | undefined
    >()
    const [addEntityRecord, setAddEntityRecord] = useState<
      boolean | undefined
    >()
    const [editEntityRecord, setEditEntityRecord] = useState<
      boolean | undefined
    >()
    const [isViewClicked, setIsViewClicked] = useState(false)
    const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false)
    const [viewDuplicateRecord, setViewDuplicateRecord] = useState(false)
    const [editLayout, setEditLayout] = useState<LayoutResponseType>()
    const [viewLayout, setViewLayout] = useState<LayoutResponseType>()
    const [listingLayout, setListingLayout] = useState<LayoutResponseType>()
    const [editRecordData, setEditRecordData] = useState<FieldValues>()
    const [addedRecord, setAddedRecord] = useState<FieldValues | null>(null)
    const [dupData, setDupData] = useState<FieldValues>()
    const [selectedRow, setSelectedRow] = useState<ListingTableRow | null>(null)
    const [refetchKey, setRefetchKey] = useState(0)
    const [triggerSearch, setTriggerSearch] = useState(false)
    const formSubmitRef = useRef<HTMLButtonElement>(null)
    const noopSetValue = noop as unknown as UseFormSetValue<FieldValues>
    const noopSetAdvancedSearch = useCallback(() => undefined, [])

    const resetSubFlow = useCallback(() => {
      setViewEntityRecord(undefined)
      setAdvancedFiltersData(undefined)
      setAddEntityRecord(false)
      setEditEntityRecord(false)
      setIsViewClicked(false)
      setIsDuplicateModalOpen(false)
      setViewDuplicateRecord(false)
      setAddedRecord(null)
      setSelectedRow(null)
    }, [])

    useImperativeHandle(ref, () => ({
      reset: resetSubFlow,
    }))

    const handlePatientSelected = useCallback(
      (record: DataTypeStore) => {
        if (isSelectionDisabled) {
          return
        }

        const resolvedPatientId = Number(record.record_id ?? record.id ?? 0)
        if (!resolvedPatientId) {
          return
        }

        resetSubFlow()
        setViewEntityRecord(record)
        onPatientSelected(record)
      },
      [isSelectionDisabled, onPatientSelected, resetSubFlow]
    )

    const fetchPatientEditLayout = useCallback(async () => {
      try {
        dispatch(showLoader())
        const res = await getLayoutByEntityIdAndTypeApi({
          entityId: ENTITY_ID.PATIENT.toString(),
          type: LAYOUT_TYPE_NAMESPACE.VIEW_EDIT,
          isDefault: true,
        })
        if (res?.data?.[0]) {
          setEditLayout(res.data[0])
        }
      } catch (error) {
        console.error(error)
      } finally {
        dispatch(hideLoader())
      }
    }, [dispatch])

    const fetchPatientListingLayout = useCallback(async () => {
      try {
        const res = await getLayoutByEntityIdAndTypeApi({
          entityId: ENTITY_ID.PATIENT.toString(),
          type: LAYOUT_TYPE_NAMESPACE.LAYOUT_LISTING,
          isDefault: true,
        })
        if (res?.data?.[0]) {
          setListingLayout(res.data[0])
          return res.data[0]
        }
      } catch (error) {
        console.error(error)
      }
    }, [])

    const fetchEditRecordData = useCallback(
      async (layoutId: number, recordId?: string | number) => {
        try {
          dispatch(showLoader())
          const res = await getEntityData(
            PATIENT_SEARCH_ELEMENT.meta.relationship?.target_entity ?? '',
            ENTITY_ID.PATIENT,
            layoutId,
            recordId ??
              (
                viewEntityRecord?.record_id ??
                viewEntityRecord?.id ??
                0
              ).toString()
          )
          if (res?.data) {
            setEditRecordData(res.data)
          }
        } catch (error) {
          console.error(error)
        } finally {
          dispatch(hideLoader())
        }
      },
      [dispatch, viewEntityRecord]
    )

    const fetchViewLayout = useCallback(async () => {
      dispatch(showLoader())
      try {
        const res = await getLayoutByEntityIdAndTypeApi({
          entityId: ENTITY_ID.PATIENT.toString(),
          type: LAYOUT_TYPE_NAMESPACE.OVERVIEW_TYPE,
          isDefault: true,
        })
        if (res?.data?.[0]) {
          setViewLayout(res.data[0])
        }
        await fetchEditRecordData(res?.data?.[0]?.id ?? 0)
        return res?.data?.[0]?.id
      } catch (error) {
        console.error(error)
      } finally {
        dispatch(hideLoader())
      }
    }, [dispatch, fetchEditRecordData])

    const createNewPatientRecord = useCallback(
      async (values?: FieldValues) => {
        if (!editLayout?.id) {
          return
        }

        try {
          const res = await addEntityData(
            editLayout.entity_name ?? '',
            ENTITY_ID.PATIENT,
            editLayout.id,
            addedRecord ?? values ?? {}
          )

          if (!res?.data?.id) {
            return
          }

          dispatch(
            addToaster({
              id: uniqueId(),
              message: TOASTER_NAMESPACE.ADD_SUCCESS,
              type: 'success',
            })
          )

          handlePatientSelected({
            id: res.data.id,
            record_id: res.data.id,
          } as DataTypeStore)
        } catch (error) {
          console.error(error)
        }
      },
      [addedRecord, dispatch, editLayout, handlePatientSelected]
    )

    const fetchPatientDuplicateTableData = useCallback(
      async (
        values: FieldValues,
        listingLayoutId: string | number,
        payload?: getEntityDataByEntityFilter['payload'] & { layout_id: number }
      ) => {
        dispatch(showLoader())
        try {
          if (!listingLayoutId) {
            return {
              content: [],
              totalElements: 0,
            }
          }

          const duplicateApiPayload = transformToDuplicateApiPayload(
            values,
            ENTITY_ID.PATIENT.toString(),
            listingLayoutId.toString(),
            editLayout
          )
          const res = await fetchDuplicateRecordsAPI(
            ENTITY_ID.PATIENT,
            duplicateApiPayload,
            payload?.page as number,
            payload?.size as number
          )
          const content = res?.data?.content?.map((row: ListingTableRow) => ({
            ...row,
            select: row.id === selectedRow?.id,
          }))
          setDupData(res?.data)
          res.data.content = content

          if (res?.data?.content?.length) {
            setIsDuplicateModalOpen(true)
          } else {
            await createNewPatientRecord(values)
          }

          return res?.data
        } catch (error) {
          console.error(error)
          return {
            content: [],
            totalElements: 0,
          }
        } finally {
          dispatch(hideLoader())
        }
      },
      [createNewPatientRecord, dispatch, editLayout, selectedRow?.id]
    )

    const editSelectedRecordAdvanceSearch = useCallback(
      async (values: FieldValues) => {
        try {
          dispatch(showLoader())
          if (editEntityRecord) {
            const res = await editEntityData(
              editLayout?.entity_name ?? '',
              ENTITY_ID.PATIENT,
              Number(editLayout?.id ?? 0),
              values,
              (
                viewEntityRecord?.record_id ??
                viewEntityRecord?.id ??
                0
              ).toString()
            )
            if (res?.data) {
              dispatch(
                addToaster({
                  id: uniqueId(),
                  message: TOASTER_NAMESPACE.EDIT_SUCCESS,
                  type: 'success',
                })
              )
              resetSubFlow()
              setTriggerSearch((prev) => !prev)
            }
          }

          if (addEntityRecord) {
            const entity = entitiesByName[editLayout?.entity_name ?? '']
            if (validatePhoneNumberAndAddress(values, dispatch)) {
              return
            }

            if (entity?.is_duplicate) {
              const patientListingLayout = await fetchPatientListingLayout()
              setAddedRecord(values)
              await fetchPatientDuplicateTableData(
                values,
                patientListingLayout?.id ?? 0
              )
              setEditEntityRecord(false)
              setIsViewClicked(false)
            } else {
              await createNewPatientRecord(values)
            }
          }
        } catch (error) {
          console.error(error)
        } finally {
          dispatch(hideLoader())
        }
      },
      [
        addEntityRecord,
        createNewPatientRecord,
        dispatch,
        editEntityRecord,
        editLayout,
        entitiesByName,
        fetchPatientDuplicateTableData,
        fetchPatientListingLayout,
        resetSubFlow,
        viewEntityRecord,
      ]
    )

    const triggerFormSubmit = useCallback(() => {
      formSubmitRef.current?.click()
    }, [])

    useEffect(() => {
      if (!isActive) {
        resetSubFlow()
      }
    }, [isActive, resetSubFlow])

    useEffect(() => {
      if (!isActive) {
        return
      }
      void fetchPatientEditLayout()
    }, [fetchPatientEditLayout, isActive])

    useEffect(() => {
      if (!isActive) {
        return
      }

      if (editEntityRecord || addEntityRecord) {
        void fetchPatientEditLayout()
      }
      if (viewEntityRecord && isViewClicked) {
        void fetchViewLayout()
      }
    }, [
      addEntityRecord,
      editEntityRecord,
      fetchPatientEditLayout,
      fetchViewLayout,
      isActive,
      isViewClicked,
      viewEntityRecord,
    ])

    useEffect(() => {
      if (editEntityRecord && editLayout?.id && viewEntityRecord) {
        void fetchEditRecordData(editLayout.id)
      }
    }, [
      editEntityRecord,
      editLayout?.id,
      fetchEditRecordData,
      viewEntityRecord,
    ])

    return (
      <div className="flex flex-col flex-1">
        <div className="flex justify-between items-center pb-2 mb-2">
          {addEntityRecord || editEntityRecord || isViewClicked ? (
            <div className="flex items-center">
              <span
                className="font-medium text-base font-primary cursor-pointer"
                onClick={resetSubFlow}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    resetSubFlow()
                  }
                }}
                role="button"
                tabIndex={0}
              >
                {breadcrumbLabel}
              </span>
              <span className="font-medium text-base font-primary text-primary-500">
                {isViewClicked
                  ? ` / ${NAMESPACES.VIEW} ${NAMESPACES.PATIENT_KEY}`
                  : editEntityRecord
                  ? ` / ${NAMESPACES.EDIT} ${NAMESPACES.PATIENT_KEY}`
                  : ` / ${NAMESPACES.ADD} ${NAMESPACES.PATIENT_KEY}`}
                {addEntityRecord && isDuplicateModalOpen
                  ? ` / ${APPLICATION_PREFERENCES_STRINGS.DUPLICATE_HEADING}`
                  : ''}
              </span>
            </div>
          ) : (
            <span className="font-medium text-base font-primary text-primary-500">
              {breadcrumbLabel}
            </span>
          )}
          <div className="flex gap-2.5">
            {isViewClicked ? (
              <>
                <CustomButton onClick={resetSubFlow}>
                  {NAMESPACES.CANCEL_BUTTON}
                </CustomButton>
                <CustomButton
                  isFilled
                  onClick={() => {
                    if (viewEntityRecord) {
                      handlePatientSelected(viewEntityRecord)
                    }
                  }}
                >
                  {APPLICATION_PREFERENCES_STRINGS.SELECT}
                </CustomButton>
              </>
            ) : editEntityRecord ? (
              <>
                <CustomButton onClick={resetSubFlow}>
                  {NAMESPACES.CANCEL_BUTTON}
                </CustomButton>
                <CustomButton isFilled onClick={triggerFormSubmit}>
                  {BUTTONS.UPDATE}
                </CustomButton>
              </>
            ) : addEntityRecord ? (
              isDuplicateModalOpen ? (
                <>
                  <CustomButton
                    isFilled
                    onClick={() => {
                      void createNewPatientRecord()
                    }}
                  >
                    {APPLICATION_PREFERENCES_STRINGS.CREATE_ANYWAY}
                  </CustomButton>
                  <CustomButton
                    isFilled
                    onClick={() => {
                      if (selectedRow) {
                        handlePatientSelected(selectedRow as DataTypeStore)
                      }
                    }}
                    disabled={isEmpty(selectedRow)}
                  >
                    {APPLICATION_PREFERENCES_STRINGS.USE_EXISTING}
                  </CustomButton>
                </>
              ) : (
                <>
                  <CustomButton onClick={resetSubFlow}>
                    {NAMESPACES.CANCEL_BUTTON}
                  </CustomButton>
                  <CustomButton isFilled onClick={triggerFormSubmit}>
                    {APPLICATION_PREFERENCES_STRINGS.NEXT}
                  </CustomButton>
                </>
              )
            ) : (
              <>
                <CustomButton onClick={onCancel}>
                  {NAMESPACES.CANCEL_BUTTON}
                </CustomButton>
                <CustomButton isFilled onClick={onProceedToIntake}>
                  {NAMESPACES.PROCEED_TO_INTAKE}
                </CustomButton>
              </>
            )}
          </div>
        </div>
        <AdvancedSearchTab
          defaultAdvancedSearch={false}
          element={PATIENT_SEARCH_ELEMENT}
          editEntityRecord={editEntityRecord}
          addEntityRecord={addEntityRecord}
          isViewClicked={isViewClicked}
          isDuplicateModalOpen={isDuplicateModalOpen}
          viewDuplicateRecord={viewDuplicateRecord}
          editLayout={editLayout}
          viewLayout={viewLayout}
          listingLayout={listingLayout}
          editRecordData={editRecordData}
          addedRecord={addedRecord}
          viewEntityRecord={viewEntityRecord}
          advancedFiltersData={advancedFiltersData}
          dupData={dupData}
          selectedRow={selectedRow}
          refetchKey={refetchKey}
          buttonRef={formSubmitRef}
          triggerSearch={triggerSearch}
          showHeader={false}
          cancelClick={resetSubFlow}
          triggerClick={triggerFormSubmit}
          editSelectedRecordAdvanceSearh={editSelectedRecordAdvanceSearch}
          createNewRecord={createNewPatientRecord}
          setAdvancedFiltersData={setAdvancedFiltersData}
          setViewEntityRecord={setViewEntityRecord}
          setEditEntityRecord={setEditEntityRecord}
          setAddEntityRecord={setAddEntityRecord}
          setIsViewClicked={setIsViewClicked}
          setIsDuplicateModalOpen={setIsDuplicateModalOpen}
          setSelectedRow={setSelectedRow}
          setValue={noopSetValue}
          setIsAdvancedSearch={noopSetAdvancedSearch}
          setRefetchKey={setRefetchKey}
          setViewDuplicateRecord={setViewDuplicateRecord}
          fetchTableDataCallback={fetchPatientDuplicateTableData}
          fetchViewLayout={fetchViewLayout}
          fetchEditRecordData={fetchEditRecordData}
          onLookupRowSelect={handlePatientSelected}
          onRecordSelected={handlePatientSelected}
          selectedProgramId={programId}
          selectedProductId={productId}
        />
      </div>
    )
  }
)

PatientAdvancedSearchPanel.displayName = 'PatientAdvancedSearchPanel'
